import {
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  type LucideIcon,
} from "lucide-react"
import type { ReactNode } from "react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { Disc, type PersonShown } from "./identity.tsx"
import { useTipsShown } from "./without-tips.tsx"

/*
 * A pull request on GitHub as Beacon shows one beside its work: a glyph for where it stands, on a
 * Board card and in its row in a ticket's Pull requests, which opens it on GitHub. Its state's
 * words live here.
 */

/** Where a pull request stands, in the four states Beacon shows. */
export type PullRequestStateShown = "draft" | "open" | "merged" | "closed"

const states: Record<PullRequestStateShown, { icon: LucideIcon; word: string; colour: string }> = {
  draft: { icon: GitPullRequestDraftIcon, word: "Draft", colour: "text-pull-request-draft" },
  open: { icon: GitPullRequestIcon, word: "Open", colour: "text-pull-request-open" },
  merged: { icon: GitMergeIcon, word: "Merged", colour: "text-pull-request-merged" },
  closed: { icon: GitPullRequestClosedIcon, word: "Closed", colour: "text-pull-request-closed" },
}

/**
 * A pull request's state as its glyph, in the state's colour, with its word in a tooltip and for a
 * screen reader: a Board card's PR mark, there for a screen reader alone, and the lead of its row
 * in a ticket's Pull requests.
 */
export function PullRequestMark({ state }: { state: PullRequestStateShown }) {
  const { icon: Icon, word, colour } = states[state]
  const said = `${word} pull request`
  const tips = useTipsShown()
  const glyph = <Icon aria-hidden className={cn("size-3.5", colour)} />
  if (!tips)
    return (
      <span role="img" aria-label={said} className="inline-flex">
        {glyph}
      </span>
    )
  return (
    <Tooltip>
      <TooltipTrigger render={<span role="img" aria-label={said} className="inline-flex" />}>
        {glyph}
      </TooltipTrigger>
      <TooltipContent>{said}</TooltipContent>
    </Tooltip>
  )
}

/**
 * A pull request as a ticket lists it: its state's glyph (its word in a tooltip) and its title,
 * then its author (the Member whose GitHub account it is, by photo and name, or else the GitHub login)
 * and its repository's name and number. The row opens it on GitHub in a new tab. `trailing` holds
 * an action on it, such as the × that unlinks it.
 */
export function PullRequestRow({
  pull,
  trailing,
}: {
  pull: {
    /** Its repository's full name, `owner/name`. */
    repository: string
    number: number
    title: string
    state: PullRequestStateShown
    /**
     * Its author: their GitHub login, and the Member whose GitHub Identity it is, or none; none at
     * all when GitHub names nobody.
     */
    author: { login: string; member: PersonShown | null } | null
    url: string
  }
  trailing?: ReactNode
}) {
  const name = pull.repository.split("/").at(-1) ?? pull.repository
  const said = states[pull.state].word.toLowerCase()
  return (
    <div className="group/row flex items-center rounded-md transition-colors duration-100 ease-snappy hover:bg-accent has-[[data-row]:focus-visible]:bg-accent">
      <a
        href={pull.url}
        target="_blank"
        rel="noreferrer"
        data-row
        aria-label={`${pull.repository}#${pull.number} on GitHub, ${said}: ${pull.title}`}
        className="flex min-w-0 flex-1 flex-col gap-0.5 rounded-md px-2 py-1.5 outline-none"
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <PullRequestMark state={pull.state} />
          <span className="truncate text-ui text-foreground">{pull.title}</span>
        </span>
        <span className="flex min-w-0 items-center gap-1.5 pl-5 text-xs text-subtle-foreground">
          {/* Who opened it shows whole; the repository's name gives way, never its number. */}
          {pull.author?.member ? (
            <span className="flex shrink-0 items-center gap-1">
              <Disc person={pull.author.member} size={16} />
              {pull.author.member.name}
            </span>
          ) : (
            pull.author && <span className="shrink-0">{pull.author.login}</span>
          )}
          {pull.author && <span aria-hidden>·</span>}
          <span className="flex min-w-0 tabular-nums">
            <span className="truncate">{name}</span>
            <span className="shrink-0">#{pull.number}</span>
          </span>
        </span>
      </a>
      {trailing && <span className="mr-1 flex shrink-0 items-center">{trailing}</span>}
    </div>
  )
}
